import { ReactNode, useEffect, useRef } from 'react'

import { createPortal } from 'react-dom'

import { IconClose } from '../../icons'
import { IconButton } from '../button'

import styles from './BottomSheet.module.css'
import { useBottomSheetDismiss, useSheetA11y } from './useBottomSheetDismiss'

const DEFAULT_BACKDROP_Z_INDEX = 9990
const DEFAULT_SHEET_Z_INDEX = 9991

export type BottomSheetProps = {
  /** Whether the sheet is visible. Returns null when false. */
  isOpen: boolean
  /** Called when the user dismisses (close button, backdrop, ESC, drag-down). */
  onClose: () => void
  /** Called after the sheet transitions from open to closed. */
  onClosed?: () => void
  /** Required: announces the sheet to screen readers. */
  ariaLabel: string
  /** Sheet content rendered inside the scrollable area. */
  children: ReactNode
  /**
   * Optional content rendered above the scroll area, inside the drag
   * region (so the gesture engages when dragging the header). Typical
   * use: a title bar + icon.
   */
  header?: ReactNode
  /**
   * Hide the close button. Default: false. Use when content has its own
   * dismiss action and a close button would be redundant.
   */
  hideClose?: boolean
  /** Aria label for the close button. Default: "Close". */
  closeAriaLabel?: string
  /**
   * z-index for the sheet. Backdrop renders one level below. Defaults are
   * sized to sit above app chrome but below toast / system overlays.
   */
  zIndex?: number
  /** Render at full viewport height instead of leaving top breathing room. */
  isFullscreen?: boolean
  /** Whether tapping the backdrop should close the sheet. Default: true. */
  dismissOnClickOutside?: boolean
  /** Minimum downward drag distance to dismiss. Default: 80px. */
  minDismissPx?: number
  /** Drag distance as a fraction of sheet height that also dismisses. Default: 0.25. */
  dismissHeightRatio?: number
  /**
   * Optional className applied to the inner scrollable container — handy
   * when content needs special padding or background that should scroll
   * with it.
   */
  scrollAreaClassName?: string
}

/**
 * Mobile bottom-sheet drawer. Anchored to the bottom of the viewport,
 * with a drag handle, drag-down to dismiss, ESC, body-scroll lock, and
 * tap-outside on the backdrop. Portaled to body so it escapes any
 * stacking context the consumer may sit inside (sidebars, navigators).
 *
 * Use this when you need full-screen modal-class content on mobile that
 * the user can pull down to close. For desktop popovers / centered
 * modals, pair with Harmony `Popup` / `Modal` and switch on viewport.
 */
export const BottomSheet = ({
  isOpen,
  onClose,
  onClosed,
  ariaLabel,
  children,
  header,
  hideClose = false,
  closeAriaLabel = 'Close',
  zIndex = DEFAULT_SHEET_Z_INDEX,
  isFullscreen = false,
  dismissOnClickOutside = true,
  minDismissPx,
  dismissHeightRatio,
  scrollAreaClassName
}: BottomSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragRegionRef = useRef<HTMLDivElement>(null)
  const wasOpenRef = useRef(isOpen)

  useSheetA11y({ isOpen, onClose })

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      onClosed?.()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, onClosed])

  // Ref objects are stable — pass `enabled: isOpen` so the gesture effect
  // re-runs after open when drag/scroll nodes exist (see hook JSDoc).
  const { isDragging } = useBottomSheetDismiss({
    sheetRef,
    dragRegionRef,
    scrollRef,
    enabled: isOpen,
    minDismissPx,
    dismissHeightRatio,
    onDismiss: onClose
  })

  if (!isOpen) return null

  // React synthetic events bubble through the React tree, not the DOM tree.
  // Since BottomSheet portals to document.body, its touch/mouse events would
  // otherwise propagate to whatever React parent the consumer rendered us
  // inside (e.g. a side-drawer panel with its own swipe-to-dismiss). Halt
  // them at the sheet boundary so the consumer's gesture handlers don't see
  // touches that visually occurred over the sheet/backdrop.
  const stopReactTreeBubble = (e: React.SyntheticEvent) => e.stopPropagation()

  const sheet = (
    <>
      <div
        className={styles.backdrop}
        onClick={dismissOnClickOutside ? onClose : undefined}
        onTouchStart={stopReactTreeBubble}
        onTouchMove={stopReactTreeBubble}
        onTouchEnd={stopReactTreeBubble}
        onMouseDown={stopReactTreeBubble}
        aria-hidden
        style={{ zIndex: zIndex - 1 }}
      />
      <div
        ref={sheetRef}
        className={
          isFullscreen
            ? `${styles.sheet} ${styles.sheetFullscreen}`
            : styles.sheet
        }
        role='dialog'
        aria-modal='true'
        aria-label={ariaLabel}
        onTouchStart={stopReactTreeBubble}
        onTouchMove={stopReactTreeBubble}
        onTouchEnd={stopReactTreeBubble}
        onMouseDown={stopReactTreeBubble}
        style={{ zIndex }}
        data-dragging={isDragging || undefined}
      >
        {hideClose ? null : (
          <IconButton
            aria-label={closeAriaLabel}
            icon={IconClose}
            color='subdued'
            size='s'
            onClick={onClose}
            className={styles.closeButton}
            data-no-drag
          />
        )}

        <div
          ref={dragRegionRef}
          className={
            header
              ? `${styles.dragRegion} ${styles.dragRegionWithHeader}`
              : styles.dragRegion
          }
        >
          <div className={styles.dragHandleArea}>
            <div className={styles.dragHandle} aria-hidden />
          </div>
          {header}
        </div>
        <div
          ref={scrollRef}
          className={
            scrollAreaClassName
              ? `${styles.scrollArea} ${scrollAreaClassName}`
              : styles.scrollArea
          }
        >
          {children}
        </div>
      </div>
    </>
  )

  return createPortal(sheet, document.body)
}

// Re-export hooks so consumers building variant sheets (e.g. one without
// the standard close button) can compose with the same gesture/a11y
// behavior without rebuilding it.
export { useBottomSheetDismiss, useSheetA11y } from './useBottomSheetDismiss'

// Default z-indices exposed so callers stacking sheets can offset relative
// to them.
export const BOTTOM_SHEET_Z_INDEX = {
  BACKDROP: DEFAULT_BACKDROP_Z_INDEX,
  SHEET: DEFAULT_SHEET_Z_INDEX
}
